<template>
    <div v-if="loading" class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-2">
        <div v-for="n in 5" :key="n" :class="[n == 1 && 'tw-from-emerald-600 tw-to-emerald-300 tw-bg-gradient-to-br']" class="tw-bg-white tw-p-4 tw-h-[81px] tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <div :class="[n == 1 && 'tw-bg-emerald-300']" class="tw-bg-gray-200 tw-animate-pulse tw-h-5 tw-w-1/3 tw-mb-2 tw-rounded-sm"></div>
            <div :class="[n == 1 && 'tw-bg-emerald-300']" class="tw-bg-gray-200 tw-animate-pulse tw-h-6 tw-w-1/2 tw-rounded-sm"></div>
        </div>
    </div>
    <div v-else class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-2">
        <!-- Total Followups -->
        <div class="tw-from-emerald-600 tw-to-emerald-300 tw-bg-gradient-to-br tw-text-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-emerald-200">
            <p class="tw-text-white">{{ $t('common.followups') }}</p>
            <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(totalFollowups, {}) }}</div>
        </div>
        
        <!-- New Followups -->
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.new') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(newFollowups, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-blue-500">{{ newPercentage }}%</div>
            </div>
        </div>
        
        <!-- Reconfirmed -->
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.reconfirmed') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(reconfirmed, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-emerald-500">{{ reconfirmedPercentage }}%</div>
            </div>
        </div>
        
        <!-- Delivered -->
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.delivered') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(delivered, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-emerald-500">{{ deliveredPercentage }}%</div>
            </div>
        </div>
        
        <!-- Canceled -->
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.canceled') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(canceled, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-rose-500">{{ canceledPercentage }}%</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { followupManager } from '@/api/dashboard';
import { ref, computed, inject, watch } from 'vue'

const loading = ref(true);
const data = ref([]);
const filters = inject('filters');

const totalFollowups = computed(() => data.value.reduce((acc, item) => acc + item.total_orders, 0))

const newFollowups = computed(() => data.value.filter(item => item.followup_status === 'new').reduce((acc, item) => acc + item.total_orders, 0))
const reconfirmed = computed(() => data.value.filter(item => item.followup_status === 'reconfirmed').reduce((acc, item) => acc + item.total_orders, 0))
const canceled = computed(() => data.value.filter(item => item.followup_status === 'canceled').reduce((acc, item) => acc + item.total_orders, 0))
const delivered = computed(() => data.value.filter(item => item.delivery_status === 'delivered').reduce((acc, item) => acc + item.total_orders, 0))

const newPercentage = computed(() => totalFollowups.value > 0 ? (newFollowups.value / totalFollowups.value * 100).toFixed(2) : '0.00')
const reconfirmedPercentage = computed(() => totalFollowups.value > 0 ? (reconfirmed.value / totalFollowups.value * 100).toFixed(2) : '0.00')
const canceledPercentage = computed(() => totalFollowups.value > 0 ? (canceled.value / totalFollowups.value * 100).toFixed(2) : '0.00')
const deliveredPercentage = computed(() => reconfirmed.value > 0 ? (delivered.value / reconfirmed.value * 100).toFixed(2) : '0.00')

const formatNumber = (number, type = {style: 'currency', currency: 'USD'}) => {
    const num = typeof number === 'number' ? number : 0
    return new Intl.NumberFormat('en', { 
        maximumFractionDigits: 2,
        ...(number === 0 && type.style === 'currency' ? { style: 'decimal' } : type)
    }).format(num)
}

const getData = async () => {
    loading.value = true
    try {
        const params = {
            ...filters.value,
            ...(filters.value.followup_id && filters.value.followup_id.length > 0 && { followup_id: filters.value.followup_id }),
        }
        const response = await followupManager.performance(params)
        data.value = response.data.data
        
    } catch (error) {
        console.error(error)
        data.value = []
    } finally {
        loading.value = false
    }
}

// Watch for filter changes to refresh data
watch(() => filters.value, () => {
    getData()
}, { deep: true })

getData()
</script> 